import { Bot, CreditCard, UserPlus, Users } from 'lucide-react-native';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '../../../components/primitives';
import type { NotificationItem as NotificationItemType } from '../../../lib/api';
import { colors } from '../../../theme/colors';
import { withAlpha } from '../../../theme/colorUtils';
import { tokens } from '../../../theme/tokens';

const CATEGORY_ICONS: Record<string, typeof UserPlus> = {
  lead_activity: UserPlus,
  agent_automation: Bot,
  team_organization: Users,
  billing_system: CreditCard,
};

const CATEGORY_COLORS: Record<string, string> = {
  lead_activity: colors.primary[500],
  agent_automation: colors.status.warning.main,
  team_organization: colors.status.info.main,
  billing_system: colors.status.success.main,
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface Props {
  notification: NotificationItemType;
  onPress: (notification: NotificationItemType) => void;
}

export function NotificationItem({ notification, onPress }: Readonly<Props>) {
  const Icon = CATEGORY_ICONS[notification.category] || CreditCard;
  const iconColor = CATEGORY_COLORS[notification.category] || colors.text.secondary;
  const isUnread = !notification.readAt;

  return (
    <Pressable
      onPress={() => onPress(notification)}
      style={({ pressed }) => [styles.container, pressed && styles.pressed]}
      accessibilityRole="button"
      accessibilityLabel={`${notification.title}: ${notification.body}`}
    >
      <View style={[styles.accentBar, isUnread && { backgroundColor: colors.primary.neon }]} />

      <View style={[styles.iconWrapper, { backgroundColor: withAlpha(iconColor, 0.13) }]}>
        <Icon size={14} color={iconColor} strokeWidth={1.8} />
      </View>

      <View style={styles.content}>
        <Text
          variant="bodySmall"
          color={colors.text.primary}
          style={isUnread ? styles.titleUnread : styles.title}
          numberOfLines={1}
        >
          {notification.title}
        </Text>
        <Text variant="caption" color={colors.text.tertiary} numberOfLines={2}>
          {notification.body}
        </Text>
        <Text variant="caption" color={colors.text.disabled} style={styles.timestamp}>
          {timeAgo(notification.createdAt)}
        </Text>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: tokens.spacing.sm,
    paddingLeft: tokens.spacing.md,
    paddingRight: tokens.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.subtle,
  },
  pressed: {
    opacity: 0.7,
  },
  // 3px vertical bar on the left edge — present (transparent) on every row
  // so the icon and content stay horizontally aligned regardless of read
  // state. The bar lights up via inline backgroundColor when unread.
  accentBar: {
    width: 3,
    alignSelf: 'stretch',
    borderRadius: 2,
    backgroundColor: 'transparent',
    marginRight: tokens.spacing.sm,
  },
  iconWrapper: {
    width: 28,
    height: 28,
    borderRadius: tokens.radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: tokens.spacing.sm,
  },
  content: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  title: {
    fontWeight: '500',
  },
  titleUnread: {
    fontWeight: '600',
  },
  timestamp: {
    marginTop: 2,
  },
});
